import { useLingui } from "@lingui/react/macro";
import type { EnvironmentId } from "@t3tools/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import { toastManager } from "~/components/ui/toast";
import { VOICE_CLIP_CHUNK_MS } from "~/voiceClips/voiceClipController";
import { voiceClips } from "~/voiceClips/voiceClipStore";
import type { VoiceTranscriptionFailure } from "~/voiceTranscription";
import {
  type ActiveVoiceRecording,
  classifyMicrophoneAccessError,
  detectVoiceInputSupport,
  type MicrophoneAccessFailure,
  readMicrophonePermissionState,
  requestMicrophonePermission,
  startVoiceRecording,
} from "~/voiceRecorder";

export type VoiceInputStatus = "idle" | "requesting" | "recording" | "finishing";

export interface VoiceInput {
  readonly supported: boolean | null;
  readonly status: VoiceInputStatus;
  readonly stream: MediaStream | null;
  readonly start: () => void;
  readonly stop: () => Promise<void>;
  readonly cancel: () => void;
}

const RECORDER_STOP_TIMEOUT_MS = 5000;

interface ActiveRecording {
  readonly recording: ActiveVoiceRecording;
  readonly clipId: string;
}

export function useVoiceInput(input: {
  readonly environmentId: EnvironmentId;
  readonly targetKey: string;
}): VoiceInput {
  const { t } = useLingui();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [status, setStatusState] = useState<VoiceInputStatus>("idle");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const statusRef = useRef<VoiceInputStatus>("idle");
  const activeRef = useRef<ActiveRecording | null>(null);
  const targetKeyRef = useRef(input.targetKey);
  const environmentIdRef = useRef(input.environmentId);
  const disposedRef = useRef(false);

  useEffect(() => {
    environmentIdRef.current = input.environmentId;
  }, [input.environmentId]);

  const readStatus = useCallback((): VoiceInputStatus => statusRef.current, []);
  const setStatus = useCallback((next: VoiceInputStatus) => {
    statusRef.current = next;
    if (!disposedRef.current) setStatusState(next);
  }, []);

  const finalize = useCallback(
    async (failure?: VoiceTranscriptionFailure): Promise<void> => {
      const active = activeRef.current;
      if (!active) return;
      activeRef.current = null;
      if (!disposedRef.current) setStream(null);
      setStatus("finishing");
      try {
        const captured = await Promise.race([
          active.recording.stop(),
          new Promise<null>((resolve) =>
            window.setTimeout(() => resolve(null), RECORDER_STOP_TIMEOUT_MS),
          ),
        ]);
        if (!captured) {
          active.recording.cancel();
          await voiceClips.finishRecordingFromChunks(active.clipId);
          return;
        }
        if (captured.blob.size === 0) {
          toastManager.add({ type: "warning", title: t`Nothing was recorded` });
          await voiceClips.discard(active.clipId);
          return;
        }
        void voiceClips.finishRecording(active.clipId, captured, failure ? { failure } : undefined);
      } catch {
        await voiceClips.finishRecordingFromChunks(active.clipId);
      } finally {
        if (readStatus() === "finishing") setStatus("idle");
      }
    },
    [readStatus, setStatus, t],
  );

  const finalizeRef = useRef(finalize);
  useEffect(() => {
    finalizeRef.current = finalize;
  }, [finalize]);

  useEffect(() => {
    targetKeyRef.current = input.targetKey;
    return () => {
      void finalizeRef.current();
    };
  }, [input.targetKey]);

  useEffect(() => {
    disposedRef.current = false;
    let cancelled = false;
    void detectVoiceInputSupport().then((result) => {
      if (!cancelled) setSupported(result);
    });
    return () => {
      cancelled = true;
      disposedRef.current = true;
    };
  }, []);

  const reportMicrophoneFailure = useCallback(
    (failure: MicrophoneAccessFailure) => {
      toastManager.add(
        failure === "denied"
          ? {
              type: "error",
              title: t`Microphone access is blocked`,
              description: t`Allow the microphone for this site in the browser settings, then press the microphone again.`,
            }
          : {
              type: "error",
              title: t`Microphone is unavailable`,
              description: t`Check that a microphone is connected and not used by another application.`,
            },
      );
    },
    [t],
  );

  const start = useCallback(() => {
    if (readStatus() !== "idle") return;
    const targetKey = targetKeyRef.current;
    setStatus("requesting");
    void (async () => {
      const permission = await readMicrophonePermissionState().catch(
        (): PermissionState => "denied",
      );
      if (permission === "prompt") {
        const result = await requestMicrophonePermission();
        if (result === "granted") {
          toastManager.add({
            type: "success",
            title: t`Microphone access granted`,
            description: t`Press the microphone again to start recording.`,
          });
        } else {
          reportMicrophoneFailure(result);
        }
        setStatus("idle");
        return;
      }
      if (permission === "denied") {
        reportMicrophoneFailure("denied");
        setStatus("idle");
        return;
      }
      let clipId: string | null = null;
      const earlyChunks: Blob[] = [];
      try {
        const recording = await startVoiceRecording({
          chunkIntervalMs: VOICE_CLIP_CHUNK_MS,
          onChunk: (chunk) => {
            if (clipId) voiceClips.appendChunk(clipId, chunk);
            else earlyChunks.push(chunk);
          },
          onLimitReached: () => void finalizeRef.current({ kind: "tooLong" }),
        });
        if (disposedRef.current || targetKeyRef.current !== targetKey) {
          recording.cancel();
          setStatus("idle");
          return;
        }
        clipId = voiceClips.beginRecording({
          targetKey,
          environmentId: environmentIdRef.current,
          mimeType: recording.mimeType,
        });
        for (const chunk of earlyChunks) voiceClips.appendChunk(clipId, chunk);
        activeRef.current = { recording, clipId };
        setStream(recording.stream);
        setStatus("recording");
      } catch (error) {
        reportMicrophoneFailure(classifyMicrophoneAccessError(error));
        setStatus("idle");
      }
    })();
  }, [readStatus, reportMicrophoneFailure, setStatus, t]);

  const cancel = useCallback(() => {
    const active = activeRef.current;
    if (!active) return;
    activeRef.current = null;
    active.recording.cancel();
    setStream(null);
    setStatus("idle");
    void voiceClips.discard(active.clipId);
  }, [setStatus]);

  return { supported, status, stream, start, stop: () => finalize(), cancel };
}
